import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { ApiError } from '@/services'

/**
 * Maps a failed call's `ApiError` to a user-visible message (PX-W6.2).
 *
 * The rule (mirrors the login page's mapping): the derived `backendKey` is an
 * i18n key in the `errors` namespace; when the key is absent (or the error
 * never carried one), the status picks the network/generic fallback — NEVER
 * the raw `message` field, which can be an untranslated EN sentence the NL UI
 * must not render.
 */
export function useApiErrorMessage(): (error: ApiError | null | undefined) => string | null {
  const { t } = useTranslation('errors', { keyPrefix: '' })

  return useCallback(
    (error: ApiError | null | undefined): string | null => {
      if (error === null || error === undefined) return null
      const fallback = error.status === 0 ? 'network' : 'generic'
      const key = error.backendKey ?? fallback
      return t(key, { defaultValue: t(fallback) })
    },
    [t]
  )
}
